import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import ReactPixel from "react-facebook-pixel";
import TagManager from "react-gtm-module";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import { Calendar3, Clock, Trash, Upload } from "react-bootstrap-icons";
import { Capacitor } from "@capacitor/core";

import {
  car_key,
  modify_search_icon,
  rental_charges,
  material_outline,
  line_arrow,
  baby_seat,
  mdi_car_insurance,
  steering_wheel,
  wind_shield_protection_icon
} from "../../../assets/images";
import axiosInstance from "../../../services/axios.instance";
import { API_URL } from "../../../services/apiConfig";
import { trackEvent } from "../../../services/events";
import { setBookingValues } from "../../../Redux/BookingData/bookingDataSlice";
import {
  NonInsuranceHolderPickupLocation,
  NonInsuranceHolderRecollectionLocation,
  CustomerDetails,
  DocumentDetails,
  bookingData
} from "../../../types";
import rules from "../../../rules";
import CustomInput from "../../CustomInput";
import CustomCheckBox from "../../customCheckbox";
import RentalSummarySecondPage from "../RentalSummarySecondPage";

import "./styles.css";
import "../../NonInsuranceHolderSecondPage/datepicker-custom.css";

const UnifiedBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isAndroid = Capacitor.getPlatform();
  const locationState = location.state || {};
  const { selectedItem, merchantId, searchData } = locationState;

  // --- Cart Synchronization & Campaign Handling ---
  const [merchantId1, setMerchantId1] = useState<string>("");
  const [coordinates, setCoordinates] = useState<{
    latitude: string;
    longitude: string;
  }>({
    latitude: "",
    longitude: "",
  });
  const [shopInfo, setShopInfo] = useState<{
    shopName: string;
    shopId: string;
  }>({
    shopName: "",
    shopId: "",
  });
  const [productDetails, setProductDetails] = useState<any>(null);

  const accessToken = localStorage.getItem("access_token");

  const decodeToken = (token: any) => {
    if (token) {
      try {
        return JSON.parse(atob(token.split(".")[1]));
      } catch (e) {
        console.error("Token decoding error:", e);
        return null;
      }
    }
    return null;
  };

  const decodedToken = decodeToken(accessToken);
  const customerId = decodedToken && decodedToken["customer-id"] ? decodedToken["customer-id"] : null;

  const handleBooking = useMutation({
    mutationFn: (userData: bookingData) =>
      axiosInstance.post(`${API_URL.BOOKING_NOW}/${customerId}/carts`, userData),
  });

  const handleClear = useMutation({
    mutationFn: (mId: string) =>
      axiosInstance.get(`${API_URL.CLEAR_CART}/${customerId}/carts/${mId}/clear`),
    onSuccess: () => {
      console.log("Cart cleared successfully, creating new cart...");
      if (selectedItem) {
        const updatedPayload: bookingData = {
          product_id: selectedItem?.product_id,
          base_product_id: selectedItem?.product_id,
          product_name: selectedItem?.product_name,
          price_per_month: selectedItem?.product_details_car?.price_per_month,
          price_per_week: selectedItem?.product_details_car?.price_per_week,
          product_description: selectedItem?.product_details?.description || "",
          merchant_id: merchantId,
          quantity: 1,
          item_id: "",
          promotion_id: "",
          price: selectedItem?.vendor_product_inventories?.[0]?.price || 0,
          toppings: [],
          buy_more_pay_less: {},
          discount_event: {},
          variant: {},
          shop_id: selectedItem?.vendor_product_inventories?.[0]?.vendor_id || "",
          product_details: selectedItem?.product_details?.description || "",
        };
        handleBooking.mutate(updatedPayload);
      }
    },
    onError: (error) => {
      console.error("Error clearing cart:", error);
    }
  });

  useEffect(() => {
    const getCartData = async () => {
      try {
        const response = await axiosInstance.get(
          `${API_URL.GET_CART}/${customerId}/carts`,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        console.log("Cart data loaded:", response.data);

        if (response.data) {
          const keys = Object.keys(response.data);
          if (keys.length > 0) {
            const merchantKey = keys[0];
            setMerchantId1(merchantKey);

            const merchantData = response.data[merchantKey];
            setCoordinates({
              latitude: merchantData.shop_information?.shop_address?.latitude || "",
              longitude: merchantData.shop_information?.shop_address?.longitude || "",
            });
            setShopInfo({
              shopName: merchantData.shop_information?.shop_name || "",
              shopId: merchantData.shop_information?.shop_id || "",
            });

            if (merchantData.products && merchantData.products.length > 0) {
              setProductDetails(merchantData.products[0]);
            }

            if (locationState.fromLandingCampaign) {
              console.log("fromLandingCampaign is true and cart is not empty. Clearing cart for merchant:", merchantKey);
              handleClear.mutate(merchantKey);
            }
          } else {
            if (locationState.fromLandingCampaign && selectedItem) {
              console.log("fromLandingCampaign is true but cart is empty. Creating cart directly...");
              const updatedPayload: bookingData = {
                product_id: selectedItem?.product_id,
                base_product_id: selectedItem?.product_id,
                product_name: selectedItem?.product_name,
                price_per_month: selectedItem?.product_details_car?.price_per_month,
                price_per_week: selectedItem?.product_details_car?.price_per_week,
                product_description: selectedItem?.product_details?.description || "",
                merchant_id: merchantId,
                quantity: 1,
                item_id: "",
                promotion_id: "",
                price: selectedItem?.vendor_product_inventories?.[0]?.price || 0,
                toppings: [],
                buy_more_pay_less: {},
                discount_event: {},
                variant: {},
                shop_id: selectedItem?.vendor_product_inventories?.[0]?.vendor_id || "",
                product_details: selectedItem?.product_details?.description || "",
              };
              handleBooking.mutate(updatedPayload);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching cart data:", error);
      }
    };

    if (customerId) {
      getCartData();
    }
  }, [customerId, locationState.fromLandingCampaign]);

  const { control, setValue, getValues, trigger, formState: { isValid } } = useForm({
    mode: "onChange",
    defaultValues: {
      residentsLicenseNumber: "",
      residentsEmiratesIdNumber: "",
      touristsLicenseNumber: "",
      touristPassportNumber: "",
      touristVisaNumber: ""
    }
  });

  const { bookingValues } = useSelector((state: any) => state.bookingValues);

  const {
    data: customerData,
  } = useQuery<CustomerDetails, Error>({
    queryKey: ["details"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.CUSTOMER_DETAILS);
      return response.data;
    },
    enabled: !!location.state,
    retry: false,
    staleTime: 0,
    refetchOnMount: true,
  });

  const { data: optionsList } = useQuery<any, Error>({
    queryKey: ["optionsList"],
    queryFn: async () => {
      const response = await axiosInstance.get(
        `${API_URL.ADDITIONAL_OPTIONS}/${merchantId}`,
      );
      return response.data;
    },
    enabled: !!merchantId,
    retry: false,
    staleTime: 0,
    refetchOnMount: true,
  });

  // --- State from Step 1 & 2 ---
  const [daysDifference, setDaysDifference] = useState(0);
  const [selectedOption1, setSelectedOption1] = useState<string>("");
  const [isLicenseLoading, setIsLicenseLoading] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);

  const years = Array.from(
    { length: 20 },
    (_, i) => new Date().getFullYear() + 1 + i
  );
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: any) => (
    <div className="datepicker-custom-header">
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        type="button"
        className="datepicker-nav-btn"
      >
        {"<"}
      </button>
      <div className="datepicker-dropdowns">
        <div className="custom-select-wrapper year-wrapper">
          <div
            className="custom-select-selected"
            onClick={() => {
              setYearDropdownOpen(!yearDropdownOpen);
              setMonthDropdownOpen(false);
            }}
          >
            {date.getFullYear()}
            <span className={`arrow ${yearDropdownOpen ? "up" : "down"}`}></span>
          </div>
          {yearDropdownOpen && (
            <div className="custom-select-options scrollable-options">
              {years.map((year) => (
                <div
                  key={year}
                  className={`custom-select-option ${date.getFullYear() === year ? "selected" : ""}`}
                  onClick={() => {
                    changeYear(year);
                    setYearDropdownOpen(false);
                  }}
                >
                  {year}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="custom-select-wrapper month-wrapper">
          <div
            className="custom-select-selected"
            onClick={() => {
              setMonthDropdownOpen(!monthDropdownOpen);
              setYearDropdownOpen(false);
            }}
          >
            {months[date.getMonth()]}
            <span className={`arrow ${monthDropdownOpen ? "up" : "down"}`}></span>
          </div>
          {monthDropdownOpen && (
            <div className="custom-select-options scrollable-options">
              {months.map((month, index) => (
                <div
                  key={month}
                  className={`custom-select-option ${date.getMonth() === index ? "selected" : ""}`}
                  onClick={() => {
                    changeMonth(index);
                    setMonthDropdownOpen(false);
                  }}
                >
                  {month}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        type="button"
        className="datepicker-nav-btn"
      >
        {">"}
      </button>
    </div>
  );

  const [expiryDate, setExpiryDate] = useState<string | undefined>(undefined);
  const [expiryError, setExpiryError] = useState("");
  const [touristExpiryDate, setTouristExpiryDate] = useState<string | undefined>(undefined);
  const [touristExpiryError, setTouristExpiryError] = useState("");

  const handleExpiryDateChange = (date: Date | null) => {
    if (date) {
      const oneYearLater = new Date();
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      if (date < oneYearLater) {
        setExpiryError("Expiry date must be at least one year from the current date.");
      } else {
        setExpiryDate(date.toISOString());
        setExpiryError("");
      }
    } else {
      setExpiryDate(undefined);
      setExpiryError("");
    }
  };

  const handleTouristExpiryDateChange = (date: Date | null) => {
    if (date) {
      const oneYearLater = new Date();
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);
      if (date < oneYearLater) {
        setTouristExpiryError("Expiry date must be at least one year from the current date.");
      } else {
        setTouristExpiryDate(date.toISOString());
        setTouristExpiryError("");
      }
    } else {
      setTouristExpiryDate(undefined);
      setTouristExpiryError("");
    }
  };

  const [licenseFile, setLicenseFile] = useState<string[]>([]);
  const [emiratesIdFile, setEmiratesIdFile] = useState<string[]>([]);
  const [licenseFile2, setLicenseFile2] = useState<string[]>([]);
  const [VisitVisaFile, setVisitVisaFile] = useState<string[]>([]);
  const [PassportFile, setPassportFile] = useState<string[]>([]);

  const [licenseFileError, setLicenseFileError] = useState("");
  const [emiratesIdFileError, setEmiratesIdFileError] = useState("");
  const [licenseFile2Error, setLicenseFile2Error] = useState("");
  const [visitVisaFileError, setVisitVisaFileError] = useState("");
  const [passportFileError, setPassportFileError] = useState("");
  const [customerDocTypeError, setCustomerDocTypeError] = useState("");

  const [uploadedFile, setUploadedFile] = useState<string[]>([]);
  const [driverFeeConfig, setDriverFeeConfig] = useState<any[]>([]);
  const docsErrorSectionRef = useRef<HTMLDivElement | null>(null);

  const isStripeFeeEnabled = String(process.env.REACT_APP_STRIPE_BOOLEAN) === "true";
  const [deliveryFee, setDeliveryFee] = useState(0);

  console.log("selectedItem", selectedItem);

  const calculateTotalAmount = () => {
    if (!selectedItem?.product_details_car) return 0;
    const car = selectedItem.product_details_car;
    const m = Math.floor(daysDifference / 30);
    const rM = daysDifference % 30;
    const w = Math.floor(rM / 7);
    const d = rM % 7;
    return (m * car.price_per_month) + (w * car.price_per_week) + (d * car.price_per_day);
  };

  const calculateFeeAmounts = () => {
    if (!selectedItem?.product_details_car)
      return { totalVat: 0, totalStripeFeeFinal: 0 };

    const car = selectedItem.product_details_car;
    const months = Math.floor(daysDifference / 30);
    const remainingAfterMonths = daysDifference % 30;
    const weeks = Math.floor(remainingAfterMonths / 7);
    const remainingDays = remainingAfterMonths % 7;

    const totalVat =
      months * (car.price_per_month_vat ?? 0) +
      weeks * (car.price_per_week_vat ?? 0) +
      remainingDays * (car.price_per_day_vat ?? 0);

    const totalStripeFeeFinal =
      months * (car.price_per_month_stripeFeeFinal ?? 0) +
      weeks * (car.price_per_week_stripeFeeFinal ?? 0) +
      remainingDays * (car.price_per_day_stripeFeeFinal ?? 0);

    return { totalVat, totalStripeFeeFinal };
  };

  const { totalVat, totalStripeFeeFinal } = calculateFeeAmounts();
  const rentalTotalVat = totalVat;
  const rentalTotalStripeFeeFinal = totalStripeFeeFinal;

  const handleDataFromRentalSummary = (data: any) => {
    console.log("Data received from RentalSummary:", data);
  };

  // --- Navigation Guard & Data ---
  useEffect(() => {
    if (!location.state) {
      navigate("/customer/myaccount", { replace: true });
      return;
    }
  }, [location.state, navigate]);

  // --- Calculation Helpers ---
  useEffect(() => {
    if (searchData?.pickupDate && searchData?.dropOffDate && searchData?.selectedTime && searchData?.selectedDropTime) {
      try {
        const pDT = new Date(searchData.pickupDate);
        const dDT = new Date(searchData.dropOffDate);
        const [pH, pM] = searchData.selectedTime.split(":");
        const [dH, dM] = searchData.selectedDropTime.split(":");
        pDT.setHours(parseInt(pH), parseInt(pM));
        dDT.setHours(parseInt(dH), parseInt(dM));
        const diff = dDT.getTime() - pDT.getTime();
        const days = Math.ceil(diff / (1000 * 3600 * 24));
        setDaysDifference(days);
      } catch (e) {
        console.error("Calculation error", e);
      }
    }
  }, [searchData]);

  const getRentalTypeAndMultiplier = (days: number) => {
    if (days % 30 === 0) return { type: "MONTHLY", multiplier: days / 30 };
    if (days % 7 === 0) return { type: "WEEKLY", multiplier: days / 7 };
    return { type: "DAILY", multiplier: days };
  };

  const calculateAmount = (rate: number | undefined) => {
    if (!rate || !daysDifference) return 0;
    return rate * daysDifference;
  };

  const getDisplayAmount = (rate: number) => {
    const commission = rate * 0.05;
    const vat = (rate + commission) * 0.05;
    const sum = rate + commission + vat;
    const stripeFee = sum * 0.0299;
    const stripeVat = isStripeFeeEnabled ? stripeFee * 0.05 : 0;
    const finalAmount = sum + stripeFee + stripeVat;
    return Math.round(finalAmount - vat).toString();
  };

  const getFeeAmount = (type: string) => {
    if (!selectedItem?.product_details_car || !daysDifference) return 0;
    const car = selectedItem.product_details_car;
    const months = Math.floor(daysDifference / 30);
    const rM = daysDifference % 30;
    const weeks = Math.floor(rM / 7);
    const days = rM % 7;

    let model;
    switch (type) {
      case 'CDW': model = car.collision_damage_waiver_model; break;
      case 'SCDW': model = car.super_collision_damage_waiver_model; break;
      case 'PAI': model = car.personal_accident_insurance_model; break;
      default: return 0;
    }

    if (!model) return 0;
    const mRate = type === 'CDW' ? model.monthly_collision_damage_waiver :
      type === 'SCDW' ? model.monthly_super_collision_damage_waiver :
        model.monthly_personal_accident_insurance;
    const wRate = type === 'CDW' ? model.weekly_collision_damage_waiver :
      type === 'SCDW' ? model.weekly_super_collision_damage_waiver :
        model.weekly_personal_accident_insurance;
    const dRate = type === 'CDW' ? model.daily_collision_damage_waiver :
      type === 'SCDW' ? model.daily_super_collision_damage_waiver :
        model.daily_personal_accident_insurance;

    return (months * (mRate ?? 0)) + (weeks * (wRate ?? 0)) + (days * (dRate ?? 0));
  };

  const getInsuranceDisplay = (type: 'CDW' | 'SCDW' | 'PAI') => {
    if (!selectedItem?.product_details_car || !daysDifference) return '';
    const car = selectedItem.product_details_car;

    let model;
    switch (type) {
      case 'CDW': model = car.collision_damage_waiver_model; break;
      case 'SCDW': model = car.super_collision_damage_waiver_model; break;
      case 'PAI': model = car.personal_accident_insurance_model; break;
      default: return '';
    }
    if (!model) return '';

    if (daysDifference >= 30 && daysDifference % 30 === 0) {
      const amount = type === 'CDW' ? model.monthly_collision_damage_waiver :
        type === 'SCDW' ? model.monthly_super_collision_damage_waiver :
          model.monthly_personal_accident_insurance;
      return `${getDisplayAmount(amount || 0)} / mo`;
    } else if (daysDifference >= 7 && daysDifference % 7 === 0 && daysDifference < 30) {
      const amount = type === 'CDW' ? model.weekly_collision_damage_waiver :
        type === 'SCDW' ? model.weekly_super_collision_damage_waiver :
          model.weekly_personal_accident_insurance;
      return `${getDisplayAmount(amount || 0)} / week`;
    } else {
      const amount = type === 'CDW' ? model.daily_collision_damage_waiver :
        type === 'SCDW' ? model.daily_super_collision_damage_waiver :
          model.daily_personal_accident_insurance;
      return `${getDisplayAmount(amount || 0)} / day`;
    }
  };

  const getAdditionalDriverFee = () => {
    if (!daysDifference || !driverFeeConfig.length) return 0;
    const { type } = getRentalTypeAndMultiplier(daysDifference);
    const feeConfig = driverFeeConfig.find(c => c.frequency === type && c.merchant_id === merchantId);
    return feeConfig ? feeConfig.amount * daysDifference : 0;
  };

  // --- API / Mutation Hooks ---
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await axiosInstance.get(`${API_URL.DRIVER_FEE_CONFIG}/${merchantId}`);
        if (res.data) setDriverFeeConfig(res.data);
      } catch (err) { console.error("Config fetch error", err); }
    };
    if (merchantId) fetchConfig();
  }, [merchantId]);

  const { mutate: uploadMutate } = useMutation({
    mutationFn: (fileList: FileList) => {
      const fd = new FormData();
      Array.from(fileList).forEach(f => fd.append("document", f));
      return axiosInstance.post(API_URL.DOC_UPLOAD, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    },
    onSuccess: (res) => {
      setUploadedFile(prev => [...prev, res?.data?.uuid]);
      setIsLicenseLoading(false);
      toast.success("Uploaded successfully");
    },
    onError: () => {
      setIsLicenseLoading(false);
    }
  });

  const handleRegistration = useMutation({
    mutationFn: (userData: DocumentDetails) =>
      axiosInstance.put(API_URL.SIGNUP_MERCHANT, userData),
    onSuccess: (successResponse) => {
      console.log("Registration Success", successResponse);
    },
    onError: (error: any) => {
      console.error("Registration Error", error);
    },
  });

  const onHandleSubmitLicense = () => {
    const formattedExpiryDate = selectedOption1 === "Tourists"
      ? (touristExpiryDate || "")
      : (expiryDate || "");

    const values = getValues();
    const licence_number = selectedOption1 === "UAE Residents"
      ? values.residentsLicenseNumber
      : values.touristsLicenseNumber;

    // Map all uploaded files to customerDocs
    // Since we push to uploadedFile (uuids) sequentially, we'll map them based on our file arrays
    const imagesData: { docType: string; docId: string }[] = [];
    let uuidIndex = 0;

    if (selectedOption1 === "UAE Residents") {
      licenseFile.forEach((_, i) => { if (uploadedFile[uuidIndex]) imagesData.push({ docType: i === 0 ? "LICENSE" : `LICENSE${i}`, docId: uploadedFile[uuidIndex++] }); });
      emiratesIdFile.forEach((_, i) => { if (uploadedFile[uuidIndex]) imagesData.push({ docType: i === 0 ? "EMIRITIES" : `EMIRITIES${i}`, docId: uploadedFile[uuidIndex++] }); });
    } else {
      licenseFile2.forEach((_, i) => { if (uploadedFile[uuidIndex]) imagesData.push({ docType: i === 0 ? "LICENSE" : `LICENSE${i}`, docId: uploadedFile[uuidIndex++] }); });
      PassportFile.forEach((_, i) => { if (uploadedFile[uuidIndex]) imagesData.push({ docType: i === 0 ? "Passport" : `Passport${i}`, docId: uploadedFile[uuidIndex++] }); });
      VisitVisaFile.forEach((_, i) => { if (uploadedFile[uuidIndex]) imagesData.push({ docType: i === 0 ? "VisitVisa" : `VisitVisa${i}`, docId: uploadedFile[uuidIndex++] }); });
    }

    const userData: DocumentDetails = {
      licence_number,
      licence_expiry_date: formattedExpiryDate,
      last_level: 999,
      customerDocs: imagesData,
    };

    if (selectedOption1 === "UAE Residents") {
      userData.emirates_id = values.residentsEmiratesIdNumber;
    }

    handleRegistration.mutate(userData);
  };

  const handleDeliveryPickup = useMutation({ mutationFn: (d: any) => axiosInstance.post(API_URL.DELIVERY_ADDRESS, d) });
  const handleDeliveryReturn = useMutation({ mutationFn: (d: any) => axiosInstance.post(API_URL.DELIVERY_ADDRESS, d) });

  // --- Handlers ---
  const handleBabySeatChange = (selected: boolean) => dispatch(setBookingValues({ ...bookingValues, babySeatSelected: selected }));
  const handleAdditionalDriverChange = (selected: boolean) => dispatch(setBookingValues({ ...bookingValues, additionalDriverSelected: selected }));
  const handleWindShieldChange = (selected: boolean) => dispatch(setBookingValues({ ...bookingValues, wtpSelected: selected }));
  const handleCarRentalChange = (selected: boolean) => dispatch(setBookingValues({ ...bookingValues, isCarRentalAllowed: selected }));
  const handleCdwChange = (selected: boolean) => dispatch(setBookingValues({ ...bookingValues, cdwSelected: selected }));
  const handleScdwChange = (selected: boolean) => dispatch(setBookingValues({ ...bookingValues, scdwSelected: selected }));

  const handleFileChangeLicense = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      setIsLicenseLoading(true);
      setLicenseFile([file.name]);
      const fd = new FormData();
      fd.append("file", file);

      const dt = new DataTransfer(); dt.items.add(file);
      uploadMutate(dt.files);
      // try {
      //   const res = await axiosInstance.post(`${API_URL.OCR}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      //   if (res.data) {
      //     const { license_number, expiry_date, status } = res.data;
      //     if (status === "Expired") {
      //       toast.error("License is expired");
      //       setExpiryError("Expired license");
      //     } else {
      //       if (license_number) setValue("residentsLicenseNumber", license_number);
      //       if (expiry_date) {
      //         const [d, m, y] = expiry_date.split("/");
      //         setExpiryDate(new Date(y, m - 1, d).toISOString());
      //       }
      //     }
      //   }
      // } catch (err) { console.error("OCR Error", err); }
      // finally {
      //   setIsLicenseLoading(false);
      //   setLicenseFile([file.name]);
      //   const dt = new DataTransfer(); dt.items.add(file);
      //   uploadMutate(dt.files);
      // }
    }
  };

  const handleGenericFileChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<string[]>>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setter(prev => [...prev, ...files.map(f => f.name)]);
      const dt = new DataTransfer(); files.forEach(f => dt.items.add(f));
      uploadMutate(dt.files);
    }
  };

  const handleDeleteImageLicense = (index: number) => setLicenseFile(prev => prev.filter((_, i) => i !== index));
  const handleDeleteImageEmiratesId = (index: number) => setEmiratesIdFile(prev => prev.filter((_, i) => i !== index));
  const handleDeleteImageLicense2 = (index: number) => setLicenseFile2(prev => prev.filter((_, i) => i !== index));
  const handleDeleteImageVisitVisa = (index: number) => setVisitVisaFile(prev => prev.filter((_, i) => i !== index));
  const handleDeleteImagePassport = (index: number) => setPassportFile(prev => prev.filter((_, i) => i !== index));

  // Re-define these to be more explicit if needed, or keep as they are if logic is same

  const validateAndSubmit = async () => {
    // Document Validation
    if (!Object.keys(customerData?.customerDocs || {}).length) {
      if (!selectedOption1) {
        toast.error("Please select either UAE Residents or Tourists option.");
        if (docsErrorSectionRef.current) docsErrorSectionRef.current.scrollIntoView({ behavior: "smooth" });
        return;
      }

      const isFormValid = await trigger();
      let isCustomValid = true;

      if (selectedOption1 === "UAE Residents") {
        if (!expiryDate || expiryError) {
          setExpiryError(expiryError || "Expiry Date is required");
          isCustomValid = false;
        }
        if (licenseFile.length === 0) {
          setLicenseFileError("License copy is required");
          isCustomValid = false;
        }
        if (emiratesIdFile.length === 0) {
          setEmiratesIdFileError("Emirates ID copy is required");
          isCustomValid = false;
        }
      } else if (selectedOption1 === "Tourists") {
        if (!touristExpiryDate || touristExpiryError) {
          setTouristExpiryError(touristExpiryError || "Expiry Date is required");
          isCustomValid = false;
        }
        if (licenseFile2.length === 0) {
          setLicenseFile2Error("License copy is required");
          isCustomValid = false;
        }
        if (VisitVisaFile.length === 0) {
          setVisitVisaFileError("Visit Visa copy is required");
          isCustomValid = false;
        }
        if (PassportFile.length === 0) {
          setPassportFileError("Passport copy is required");
          isCustomValid = false;
        }
      }

      if (!isFormValid || !isCustomValid) {
        if (!isCustomValid && docsErrorSectionRef.current) {
          docsErrorSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }

      // Register documents if new
      onHandleSubmitLicense();
    }

    // Logistics confirmed
    handleDeliveryPickup.mutate({ addressType: searchData?.pickupPlace, type: "PICKUP_LOCATION", latitude: selectedItem?.merchant_latitude, longitude: selectedItem?.merchant_longitude });
    handleDeliveryReturn.mutate({ addressType: searchData?.returnPlace, type: "RECOLLECTION_LOCATION", latitude: selectedItem?.merchant_latitude, longitude: selectedItem?.merchant_longitude });

    // State prep
    const car = selectedItem?.product_details_car;
    const optionalData = {
      baby_seat: bookingValues?.babySeatSelected || false,
      baby_seat_amount: bookingValues?.babySeatSelected ? (car?.child_seat_amount || 0) : 0,
      additional_driver: bookingValues?.additionalDriverSelected || false,
      additional_driver_amount: bookingValues?.additionalDriverSelected ? (getAdditionalDriverFee() || 0) : 0,
      cdw_flag: bookingValues?.cdwSelected || false,
      scdw_flag: bookingValues?.scdwSelected || false,
      pai_insurance_flag: bookingValues?.isCarRentalAllowed || false,
      wind_shield_protection: bookingValues?.wtpSelected ? (car?.wind_shield_protection || 0) : 0,

      pai_insurance_amount: bookingValues?.isCarRentalAllowed ? (car?.personal_accident_insurance_model?.daily_personal_accident_insurance || 0) : 0,
      weekly_pai_insurance_amount: bookingValues?.isCarRentalAllowed ? (car?.personal_accident_insurance_model?.weekly_personal_accident_insurance || 0) : 0,
      monthly_pai_insurance_amount: bookingValues?.isCarRentalAllowed ? (car?.personal_accident_insurance_model?.monthly_personal_accident_insurance || 0) : 0,

      cdw_amount: bookingValues?.cdwSelected ? (car?.collision_damage_waiver_model?.daily_collision_damage_waiver || 0) : 0,
      weekly_cdw_amount: bookingValues?.cdwSelected ? (car?.collision_damage_waiver_model?.weekly_collision_damage_waiver || 0) : 0,
      monthly_cdw_amount: bookingValues?.cdwSelected ? (car?.collision_damage_waiver_model?.monthly_collision_damage_waiver || 0) : 0,

      scdw_amount: bookingValues?.scdwSelected ? (car?.super_collision_damage_waiver_model?.daily_super_collision_damage_waiver || 0) : 0,
      weekly_scdw_amount: bookingValues?.scdwSelected ? (car?.super_collision_damage_waiver_model?.weekly_super_collision_damage_waiver || 0) : 0,
      monthly_scdw_amount: bookingValues?.scdwSelected ? (car?.super_collision_damage_waiver_model?.monthly_super_collision_damage_waiver || 0) : 0,
    };

    const { totalVat, totalStripeFeeFinal } = calculateFeeAmounts();
    const enrichedSelectedItem = {
      ...selectedItem,
      product_details_car: {
        ...selectedItem.product_details_car,
        totalVat,
        totalStripeFeeFinal,
      },
    };

    TagManager.dataLayer({
      dataLayer: {
        event: "begin_checkout",
        page_type: "standard",
        page_path: window.location.pathname,
        page_url: window.location.href,
        product_id: selectedItem?.product_id,
        product_name: selectedItem?.product_name,
        car_brand: selectedItem?.product_details_car?.car_brand,
        price_per_day: selectedItem?.product_details_car?.price_per_day,
        currency: "AED",
        pickup_location: searchData?.pickupPlace,
        dropoff_location: searchData?.returnPlace,
        pickup_datetime: searchData?.pickupDate,
        dropoff_datetime: searchData?.dropOffDate,
      }
    });

    navigate("/customer/processpayment", {
      state: {
        ...location.state,
        selectedItem: enrichedSelectedItem,
        deliveryDetails: {
          formData: {
            pickUpLocation: searchData?.pickupPlace,
            returnLocation: searchData?.returnPlace,
          },
          daysDifference,
          totalAmount: calculateTotalAmount(),
          pickupDateTime: (() => {
            const d = new Date(searchData.pickupDate);
            const [h, m] = searchData.selectedTime.split(":");
            d.setHours(parseInt(h), parseInt(m));
            return d.toISOString();
          })(),
          dropDateTime: (() => {
            const d = new Date(searchData.dropOffDate);
            const [h, m] = searchData.selectedDropTime.split(":");
            d.setHours(parseInt(h), parseInt(m));
            return d.toISOString();
          })(),
          pickupTime: searchData.selectedTime,
          dropOffTime: searchData.selectedDropTime,
          pickupDateDiff: searchData.pickupDate,
          dropDateDiff: searchData.dropOffDate,
        },
        optionalData,
        additionalDriverFee: bookingValues?.additionalDriverSelected ? getAdditionalDriverFee() : 0,
        babySeatAmount: bookingValues?.babySeatSelected ? calculateAmount(selectedItem?.product_details_car?.child_seat_amount) : 0,
        cdw_Amount: bookingValues?.cdwSelected ? getFeeAmount('CDW') : 0,
        scdw_Amount: bookingValues?.scdwSelected ? getFeeAmount('SCDW') : 0,
        pai_InsuranceAmount: bookingValues?.isCarRentalAllowed ? getFeeAmount('PAI') : 0,
        wtpAmount: bookingValues?.wtpSelected ? calculateAmount(selectedItem?.product_details_car?.wind_shield_protection) : 0,
      }
    });
  };

  const formatDate = (ds: string) => {
    if (!ds) return "N/A";
    const d = new Date(ds);
    return isNaN(d.getTime()) ? ds : `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };


  return (
    <div className="unified-booking-container">
      <div className="row g-4 mb-5 mb-md-0">
        <div className="col-lg-8">
          <div className="card card-custom">
            <div className="card-body">
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-5">
                <h1 className="page-head-add mb-0">
                  <img src={car_key} alt="" className="header-icon" />
                  Review Your Booking
                </h1>
                <Link to="/customer/myaccount" state={{ searchData }} className="modify-search-link">
                  <img src={modify_search_icon} alt="" width="16" /> Modify Search
                </Link>
              </div>

              {/* Section 1: Trip Summary */}
              <div className="mb-5">
                <div className="d-flex align-items-center gap-2 mb-4">
                  <div className="section-header-bar"></div>
                  <h2 className="label_noninsurance mb-0">Trip Logistics</h2>
                </div>
                <div className="trip-info-summary">
                  <div className="div-location-time-input">
                    <div className="div-location-time-input-label-group">
                      <label className="label_noninsurance">Pickup Location</label>
                      <div className="non-insur-input-display">{searchData?.pickupPlace || "N/A"}</div>
                      <div className="trip-summary-date-time mt-2">
                        <span className="d-inline-flex align-items-center me-2">
                          <Calendar3 size={12} className="text-warning me-1" />
                          <span>{formatDate(searchData?.pickupDate || searchData?.pickupDateDiff)}</span>
                        </span>
                        <span className="d-inline-flex align-items-center">
                          <Clock size={12} className="text-warning me-1" />
                          <span>{searchData?.selectedTime || searchData?.pickupTime || "N/A"}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="div-location-time-input">
                    <div className="div-location-time-input-label-group">
                      <label className="label_noninsurance">Return Location</label>
                      <div className="non-insur-input-display">{searchData?.returnPlace || "N/A"}</div>
                      <div className="trip-summary-date-time mt-2">
                        <span className="d-inline-flex align-items-center me-2">
                          <Calendar3 size={12} className="text-warning me-1" />
                          <span>{formatDate(searchData?.dropOffDate || searchData?.dropDateDiff)}</span>
                        </span>
                        <span className="d-inline-flex align-items-center">
                          <Clock size={12} className="text-warning me-1" />
                          <span>{searchData?.selectedDropTime || searchData?.dropOffTime || "N/A"}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="div-location-time-input text-center">
                    <label className="label_noninsurance">Rental Duration</label>
                    <div className="non-insur-input-display justify-content-center">
                      <span className="booked-days-highlight">{daysDifference} Days</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Extra Services Section */}
              {(selectedItem?.product_details_car?.child_seat_amount || optionsList?.additionalDriver || selectedItem?.product_details_car?.wind_shield_protection) && (
                <div className="mb-5">
                  <div className="d-flex align-items-center gap-2 mb-4">
                    <div className="section-header-bar"></div>
                    <h2 className="label_noninsurance mb-0">Extra Services</h2>
                  </div>
                  <div className="options-grid">
                    {/* {selectedItem?.product_details_car?.child_seat_amount !== 0 && (
                      <div
                        className={`option-card ${bookingValues?.babySeatSelected ? 'selected' : ''}`}
                        onClick={() => handleBabySeatChange(!bookingValues?.babySeatSelected)}
                      >
                        <div style={{ pointerEvents: 'none' }}>
                          <CustomCheckBox
                            onChange={() => { }}
                            isChecked={bookingValues?.babySeatSelected === true}
                          />
                        </div>
                        <div className="option-content">
                          <img src={baby_seat} alt="" className="header-icon mb-2" />
                          <div className="non_insur_per_day_aed">
                            AED {getDisplayAmount(selectedItem?.product_details_car?.child_seat_amount || 0)} / day
                          </div>
                          <div className="radio-label-noninsurence">Baby Seat</div>
                        </div>
                      </div>
                    )} */}

                    {optionsList?.additionalDriver && (
                      <div
                        className={`option-card ${bookingValues?.additionalDriverSelected ? 'selected' : ''}`}
                        onClick={() => handleAdditionalDriverChange(!bookingValues?.additionalDriverSelected)}
                      >
                        <div style={{ pointerEvents: 'none' }}>
                          <CustomCheckBox
                            onChange={() => { }}
                            isChecked={bookingValues?.additionalDriverSelected === true}
                          />
                        </div>
                        <div className="option-content">
                          <img src={steering_wheel} alt="" className="header-icon mb-2" />
                          <div className="non_insur_per_day_aed">
                            AED {(() => {
                              const perDayRate = driverFeeConfig.find(
                                (config) => config.frequency === getRentalTypeAndMultiplier(daysDifference || 0).type
                              )?.amount || 0;
                              return getDisplayAmount(perDayRate);
                            })()} / day
                          </div>
                          <div className="radio-label-noninsurence">Additional Driver</div>
                        </div>
                      </div>
                    )}

                    {selectedItem?.product_details_car?.wind_shield_protection !== 0 && (
                      <div
                        className={`option-card ${bookingValues?.wtpSelected ? 'selected' : ''}`}
                        onClick={() => handleWindShieldChange(!bookingValues?.wtpSelected)}
                      >
                        <div style={{ pointerEvents: 'none' }}>
                          <CustomCheckBox
                            onChange={() => { }}
                            isChecked={bookingValues?.wtpSelected === true}
                          />
                        </div>
                        <div className="option-content">
                          <img src={wind_shield_protection_icon} alt="" className="header-icon mb-2" />
                          <div className="non_insur_per_day_aed">
                            AED {getDisplayAmount(selectedItem?.product_details_car?.wind_shield_protection || 0)} / day
                          </div>
                          <div className="radio-label-noninsurence">Windshield Protection</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Insurance Section */}
              <div className="mb-5">
                <div className="d-flex align-items-center gap-2 mb-4">
                  <div className="section-header-bar"></div>
                  <h2 className="label_noninsurance mb-0">Insurance Coverage</h2>
                </div>
                <div className="options-grid">
                  {selectedItem?.product_details_car?.personal_accident_insurance_model?.daily_personal_accident_insurance !== 0 && (
                    <div
                      className={`option-card ${bookingValues?.isCarRentalAllowed ? 'selected' : ''}`}
                      onClick={() => handleCarRentalChange(!bookingValues?.isCarRentalAllowed)}
                    >
                      <div style={{ pointerEvents: 'none' }}>
                        <CustomCheckBox
                          onChange={() => { }}
                          isChecked={bookingValues?.isCarRentalAllowed === true}
                        />
                      </div>
                      <div className="option-content">
                        <img src={mdi_car_insurance} alt="" className="header-icon mb-2" />
                        <div className="non_insur_per_day_aed">
                          AED {getInsuranceDisplay('PAI')}
                        </div>
                        <div className="radio-label-noninsurence">PAI Insurance</div>
                      </div>
                    </div>
                  )}

                  {selectedItem?.product_details_car?.collision_damage_waiver_model?.daily_collision_damage_waiver !== 0 && (
                    <div
                      className={`option-card ${bookingValues?.cdwSelected ? 'selected' : ''}`}
                      onClick={() => handleCdwChange(!bookingValues?.cdwSelected)}
                    >
                      <div style={{ pointerEvents: 'none' }}>
                        <CustomCheckBox
                          onChange={() => { }}
                          isChecked={bookingValues?.cdwSelected === true}
                        />
                      </div>
                      <div className="option-content">
                        <img src={mdi_car_insurance} alt="" className="header-icon mb-2" />
                        <div className="non_insur_per_day_aed">
                          AED {getInsuranceDisplay('CDW')}
                        </div>
                        <div className="radio-label-noninsurence">CDW Insurance</div>
                      </div>
                    </div>
                  )}

                  {selectedItem?.product_details_car?.super_collision_damage_waiver_model?.daily_super_collision_damage_waiver !== 0 && (
                    <div
                      className={`option-card ${bookingValues?.scdwSelected ? 'selected' : ''}`}
                      onClick={() => handleScdwChange(!bookingValues?.scdwSelected)}
                    >
                      <div style={{ pointerEvents: 'none' }}>
                        <CustomCheckBox
                          onChange={() => { }}
                          isChecked={bookingValues?.scdwSelected === true}
                        />
                      </div>
                      <div className="option-content">
                        <img src={mdi_car_insurance} alt="" className="header-icon mb-2" />
                        <div className="non_insur_per_day_aed">
                          AED {getInsuranceDisplay('SCDW')}
                        </div>
                        <div className="radio-label-noninsurence">SCDW Insurance</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Rental Charges & Deposit */}
              <div className="rental-charges-header">
                <img src={rental_charges} alt="" className="header-icon" />
                <h2 className="page-head-add mb-0">Rental Charges</h2>
                <img src={line_arrow} alt="" className="d-none d-xl-block" style={{ height: '2px', opacity: 0.3 }} />
              </div>

              <div className="deposit-banner">
                <img src={material_outline} alt="" className="header-icon" />
                <div className="deposit-banner-text">
                  Renter is required to make a security deposit of
                  <span className="deposit-amount"> AED {selectedItem?.product_details_car?.deposit?.toFixed(2) || "0.00"} </span>
                  before vehicle collection. This is fully refundable upon safe return.
                </div>
              </div>

              {/* Pricing Grid */}
              <div className="pricing-grid">
                {/* Daily Rate */}
                <div className="price-card">
                  <div className="price-tier">Daily Rate</div>
                  <div className="price-amount">
                    AED {selectedItem?.product_details_car?.price_per_day?.toFixed(2) || "0.00"}
                  </div>
                  <div className="price-details">
                    <div>
                      Booked for {daysDifference} Days
                    </div>
                    <div>
                      Subtotal: AED <span className="booked-days-highlight">{calculateTotalAmount()}</span>
                    </div>
                  </div>
                  <div className="price-offer-text">Total shown in summary</div>
                </div>

                {/* Weekly Rate */}
                <div className="price-card">
                  <div className="price-tier">Weekly Rate</div>
                  <div className="price-amount">
                    AED {selectedItem?.product_details_car?.price_per_week?.toFixed(2) || "0.00"}
                  </div>
                  <div className="price-details">
                    <span className="old-price">AED {((selectedItem?.product_details_car?.price_per_day || 0) * 7).toFixed(2)}</span>
                    <br />
                    Book for 7, 14, 21+ days to automatically apply this offer.
                  </div>
                </div>

                {/* Monthly Rate */}
                <div className="price-card">
                  <div className="price-tier">Monthly Rate</div>
                  <div className="price-amount">
                    AED {selectedItem?.product_details_car?.price_per_month?.toFixed(2) || "0.00"}
                  </div>
                  <div className="price-details">
                    <span className="old-price">AED {((selectedItem?.product_details_car?.price_per_day || 0) * 30).toFixed(2)}</span>
                    <br />
                    Book for 30+ days for maximum savings.
                  </div>
                </div>
              </div>

              {/* Section 4: Identity Verification */}
              {!Object.keys(customerData?.customerDocs || {}).length && (
                <div className="mb-5" ref={docsErrorSectionRef}>
                  <div className="d-flex align-items-center gap-2 mb-4">
                    <div className="section-header-bar"></div>
                    <h2 className="label_noninsurance mb-0">Identity Verification <span style={{ color: 'red' }}>*</span></h2>
                  </div>
                  <div className="doc-selection-tabs">
                    <label className={`doc-tab d-flex align-items-center justify-content-start gap-2 ${selectedOption1 === "UAE Residents" ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="identityType"
                        value="UAE Residents"
                        checked={selectedOption1 === "UAE Residents"}
                        onChange={() => setSelectedOption1("UAE Residents")}
                        className="custom-radio-input"
                      />
                      <span className="radio-custom"></span>
                      UAE Resident
                    </label>
                    <label className={`doc-tab d-flex align-items-center justify-content-start gap-2 ${selectedOption1 === "Tourists" ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="identityType"
                        value="Tourists"
                        checked={selectedOption1 === "Tourists"}
                        onChange={() => setSelectedOption1("Tourists")}
                        className="custom-radio-input"
                      />
                      <span className="radio-custom"></span>
                      Tourist
                    </label>
                  </div>

                  {selectedOption1 === "UAE Residents" && (
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="res_license_num">Driving License (Copy)</div>
                        <div className="upload-field-container">
                          {isLicenseLoading ? <div className="spinner-border text-warning"></div> : (
                            <>
                              {licenseFile.length === 0 ? (
                                <button className="btn-custom-upload" onClick={() => (document.getElementById('lic-inp') as any).click()}><Upload /> Browse</button>
                              ) : (
                                <div className="uploaded-files-list">
                                  {licenseFile.map((name, i) => (
                                    <div key={i} className="uploaded-file-item">
                                      <span className="file-name">{name}</span>
                                      <Trash className="delete-icon" onClick={() => handleDeleteImageLicense(i)} />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                          <input id="lic-inp" type="file" hidden accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onClick={(e) => { (e.target as HTMLInputElement).value = ''; }} onChange={handleFileChangeLicense} />
                          {licenseFileError && <div className="text-danger mt-1 small">{licenseFileError}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="res_license_num">Emirates ID (Copy)</div>
                        <div className="upload-field-container">
                          {emiratesIdFile.length === 0 ? (
                            <button className="btn-custom-upload" onClick={() => (document.getElementById('eid-inp') as any).click()}><Upload /> Browse</button>
                          ) : (
                            <div className="uploaded-files-list">
                              {emiratesIdFile.map((name, i) => (
                                <div key={i} className="uploaded-file-item">
                                  <span className="file-name">{name}</span>
                                  <Trash className="delete-icon" onClick={() => handleDeleteImageEmiratesId(i)} />
                                </div>
                              ))}
                            </div>
                          )}
                          <input id="eid-inp" type="file" hidden accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onClick={(e) => { (e.target as HTMLInputElement).value = ''; }} onChange={(e) => handleGenericFileChange(e, setEmiratesIdFile)} multiple />
                          {emiratesIdFileError && <div className="text-danger mt-1 small">{emiratesIdFileError}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="res_license_num">License Number</div>
                        <CustomInput
                          inputName="residentsLicenseNumber"
                          inputType="text"
                          className="ins_holder_sec_input_style"
                          placeholder="Eg: 123456"
                          control={control}
                          rules={rules.AuthRules.license_number}
                          onInputChange={(val: string) => { }}
                          editable={true}
                          showEditIcon={true}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="res_license_num">Emirates ID Number</div>
                        <CustomInput
                          inputName="residentsEmiratesIdNumber"
                          inputType="text"
                          className="ins_holder_sec_input_style"
                          placeholder="784-19XX-XXXXXXX-X"
                          control={control}
                          rules={rules.AuthRules.emirates_id}
                          onInputChange={(val: string) => { }}
                          editable={true}
                          showEditIcon={true}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="res_license_num">License Expiry Date</div>
                        <div className="date-line-noninsurence">
                          <Calendar3 className="calendar-icon-expiry" />
                          <DatePicker
                            selected={expiryDate ? new Date(expiryDate) : null}
                            onChange={handleExpiryDateChange}
                            dateFormat="EEE, MMM d, yyyy"
                            customInput={<input readOnly className="input-date" onFocus={(e) => e.target.blur()} />}
                            renderCustomHeader={renderCustomHeader}
                            popperClassName="license-expiry-datepicker"
                            popperPlacement="top-start"
                            portalId="root"
                            minDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                          />
                        </div>
                        {expiryError && <div className="text-danger small">{expiryError}</div>}
                      </div>
                    </div>
                  )}
                  {selectedOption1 === "Tourists" && (
                    <div className="row g-4">
                      <div className="col-md-6">
                        <div className="res_license_num">License Copy</div>
                        <div className="upload-field-container">
                          {licenseFile2.length === 0 ? (
                            <button className="btn-custom-upload" onClick={() => (document.getElementById('lic2-inp') as any).click()}><Upload /> Browse</button>
                          ) : (
                            <div className="uploaded-files-list">
                              {licenseFile2.map((name, i) => (
                                <div key={i} className="uploaded-file-item">
                                  <span className="file-name">{name}</span>
                                  <Trash className="delete-icon" onClick={() => handleDeleteImageLicense2(i)} />
                                </div>
                              ))}
                            </div>
                          )}
                          <input id="lic2-inp" type="file" hidden accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onClick={(e) => { (e.target as HTMLInputElement).value = ''; }} onChange={(e) => handleGenericFileChange(e, setLicenseFile2)} multiple />
                          {licenseFile2Error && <div className="text-danger mt-1 small">{licenseFile2Error}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="res_license_num">Passport Copy</div>
                        <div className="upload-field-container">
                          {PassportFile.length === 0 ? (
                            <button className="btn-custom-upload" onClick={() => (document.getElementById('pass-inp') as any).click()}><Upload /> Browse</button>
                          ) : (
                            <div className="uploaded-files-list">
                              {PassportFile.map((name, i) => (
                                <div key={i} className="uploaded-file-item">
                                  <span className="file-name">{name}</span>
                                  <Trash className="delete-icon" onClick={() => handleDeleteImagePassport(i)} />
                                </div>
                              ))}
                            </div>
                          )}
                          <input id="pass-inp" type="file" hidden accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onClick={(e) => { (e.target as HTMLInputElement).value = ''; }} onChange={(e) => handleGenericFileChange(e, setPassportFile)} multiple />
                          {passportFileError && <div className="text-danger mt-1 small">{passportFileError}</div>}
                        </div>
                      </div>
                      <div className="col-md-12">
                        <div className="res_license_num">Visit Visa Copy</div>
                        <div className="upload-field-container">
                          {VisitVisaFile.length === 0 ? (
                            <button className="btn-custom-upload" onClick={() => (document.getElementById('visa-inp') as any).click()}><Upload /> Browse</button>
                          ) : (
                            <div className="uploaded-files-list">
                              {VisitVisaFile.map((name, i) => (
                                <div key={i} className="uploaded-file-item">
                                  <span className="file-name">{name}</span>
                                  <Trash className="delete-icon" onClick={() => handleDeleteImageVisitVisa(i)} />
                                </div>
                              ))}
                            </div>
                          )}
                          <input id="visa-inp" type="file" hidden accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onClick={(e) => { (e.target as HTMLInputElement).value = ''; }} onChange={(e) => handleGenericFileChange(e, setVisitVisaFile)} multiple />
                          {visitVisaFileError && <div className="text-danger mt-1 small">{visitVisaFileError}</div>}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="res_license_num">License Number</div>
                        <CustomInput
                          inputName="touristsLicenseNumber"
                          inputType="text"
                          className="ins_holder_sec_input_style"
                          placeholder="Eg: 449236"
                          control={control}
                          onInputChange={(val: string) => { }}
                          editable={true}
                          showEditIcon={true}
                        />
                      </div>
                      <div className="col-md-6">
                        <div className="res_license_num">License Expiry Date</div>
                        <div className="date-line-noninsurence">
                          <Calendar3 className="calendar-icon-expiry" />
                          <DatePicker
                            selected={touristExpiryDate ? new Date(touristExpiryDate) : null}
                            onChange={handleTouristExpiryDateChange}
                            dateFormat="EEE, MMM d, yyyy"
                            customInput={<input readOnly className="input-date" onFocus={(e) => e.target.blur()} />}
                            renderCustomHeader={renderCustomHeader}
                            popperClassName="license-expiry-datepicker"
                            popperPlacement="top-start"
                            portalId="root"
                            minDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                          />
                        </div>
                        {touristExpiryError && <div className="text-danger small">{touristExpiryError}</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="unified-actions">
                <button className="btn all_button_style order-back_button_outline" onClick={() => navigate("/customer/myaccount", { state: { searchData } })}>BACK</button>
                <button className="btn all_button_style continue-btn" onClick={validateAndSubmit}>CONTINUE</button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-lg-4">
          <div className="sticky-sidebar">
            <RentalSummarySecondPage
              daysDifference={daysDifference}
              totalAmount={calculateTotalAmount()}
              childSeatAmount={calculateAmount(selectedItem?.product_details_car?.child_seat_amount)}
              paiInsuranceAmount={getFeeAmount('PAI')}
              cdwAmount={getFeeAmount('CDW')}
              scdwAmount={getFeeAmount('SCDW')}
              wtpAmount={calculateAmount(selectedItem?.product_details_car?.wind_shield_protection)}
              carImage={selectedItem?.product_details?.images?.[0] || ""}
              carModel={selectedItem?.product_details_car?.car_model || ""}
              seat={selectedItem?.product_details_car?.number_of_seats || 0}
              fuelType={selectedItem?.product_details_car?.fuel_type || ""}
              merchantId={merchantId}
              isLastPage={false}
              airportFee={bookingValues?.airportFee || 0}
              additionalDriverFee={getAdditionalDriverFee()}
              onDataFromRentalSummary={handleDataFromRentalSummary}
              deliveryFee={deliveryFee}
              rentalTotalVat={rentalTotalVat}
              rentalTotalStripeFeeFinal={rentalTotalStripeFeeFinal}
            />
          </div>
        </div>
      </div>

      {/* Mobile Actions */}
      <div className="unified-booking-button-mobile">
        <button className="all_button_style order-back_button_outline" onClick={() => navigate("/customer/myaccount")}>BACK</button>
        <button className="all_button_style continue-btn" onClick={validateAndSubmit}>CONTINUE</button>
      </div>
    </div>
  );
};

export default UnifiedBooking;
